import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { theme, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { reportingOp } from 'store/ducks/Reporting';
import { RootState } from 'store/configureStore';
import {
  getReportTypes,
  getReportRiskAndControlsTypes
} from 'constants/workingAreas';
import { IReportType } from 'store/ducks/Global/types';
import ControlReport from './ControlReport/ControlInventoryReport/ControlReport';
import BusinessRulesAuditFindings from './BusinessRulesAuditFindings/BusinessRulesAuditFindings';
import BruEvidences from './BruEvidences/BruEvidences';
import BruGapsAuditsSolutions from './BruGapsAuditsSolutions/BruGapsAuditsSolutions';
import SolutionsTasks from './SolutionsTasks/SolutionsTasks';
import GapsFilter from './GapsFilter/GapsFilter';
import AuditFindingsFilter from './AuditFindingsFilter/AuditFindingsFilter';
import ReportingLayout from 'components/Layouts/ReportingLayout/ReportingLayout';
import * as Styled from './Reporting.styled';
import ControlOperationalReport from './ControlReport/ControlOperationalreport/ControlReport';
import ControlDesignReport from './ControlReport/ControlDesignReport/ControlReport';
import { ReportExportProvider } from 'components/Reporting/ReportExportContext';
import ExportReportButton from 'components/Reporting/ExportReportButton';

const { useToken } = theme;

const Reporting: React.FC = () => {
  const { token } = useToken();
  const location = useLocation();
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<IReportType | null>(
    null
  );

  const { lang, activeFilters, scheme } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    activeFilters: state.global.activeFilters,
    scheme: state.global.scheme
  }));

  // Determine which reports to show based on current location/context
  const isBusinessProcessReporting =
    location.pathname.includes('internal_controls') ||
    location.pathname.includes('risk-controls');
  const reportsToShow = isBusinessProcessReporting
    ? getReportRiskAndControlsTypes(t)
    : getReportTypes(t);

  const handleReportClick = (report: IReportType) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
  };

  useEffect(() => {
    reportingOp.fetchReport(lang, activeFilters).then(() => {});
  }, [lang, activeFilters]);

  return (
    <ReportingLayout>
      <Styled.Container token={token} scheme={scheme}>
        <Styled.SubHeaderSection token={token} scheme={scheme}>
          <p>{t('GENERATE_DOWNLOAD_REPORTS')}</p>
        </Styled.SubHeaderSection>

        <Styled.ContentRow gutter={[24, 24]}>
          <Styled.MainCol xs={24} lg={10}>
            <Styled.ReportsTitle level={4}>
              {t('GENERATE_REPORTS')}
            </Styled.ReportsTitle>

            <Styled.ReportsGrid token={token} scheme={scheme}>
              {reportsToShow.map((report, index) => (
                <Styled.ReportCol key={index}>
                  <Styled.ReportCard
                    token={token}
                    scheme={scheme}
                    onClick={() => handleReportClick(report)}
                  >
                    <Styled.CardHeader />
                    <Styled.CardTitle token={token} level={5}>
                      <Styled.CardTitleWithInfo>
                        {report.title}
                      </Styled.CardTitleWithInfo>
                    </Styled.CardTitle>
                  </Styled.ReportCard>
                </Styled.ReportCol>
              ))}
            </Styled.ReportsGrid>
          </Styled.MainCol>
        </Styled.ContentRow>
        <Styled.StyledModal
          scheme={scheme}
          open={isModalOpen}
          title={selectedReport?.title}
          onCancel={handleModalClose}
          footer={null}
          width='95vw'
          style={Styled.modalStyles}
          destroyOnClose
        >
          <ReportExportProvider>
            <Styled.ExportButtonWrapper>
              <ExportReportButton />
            </Styled.ExportButtonWrapper>
            <Styled.ReportTableWrapper scheme={scheme}>
              {selectedReport?.titleKey === 'CONTROL_REPORT' && (
                <ControlReport />
              )}
              {selectedReport?.titleKey === 'CONTROL_DESIGN_REPORT' && (
                <ControlDesignReport />
              )}
              {selectedReport?.titleKey === 'CONTROL_OPERATIONAL_REPORT' && (
                <ControlOperationalReport />
              )}
              {selectedReport?.titleKey === 'BUSINESS_RULES_AUDIT_FINDINGS' && (
                <BusinessRulesAuditFindings />
              )}
              {selectedReport?.titleKey === 'BRU_EVIDENCES' && <BruEvidences />}
              {selectedReport?.titleKey === 'BRU_GAPS_AUDITS_SOLUTIONS' && (
                <BruGapsAuditsSolutions />
              )}
              {selectedReport?.titleKey === 'SOLUTIONS_TASKS' && (
                <SolutionsTasks />
              )}
              {selectedReport?.titleKey === 'GAPS_FILTER' && <GapsFilter />}
              {selectedReport?.titleKey === 'AUDIT_FINDINGS_FILTER' && (
                <AuditFindingsFilter />
              )}
            </Styled.ReportTableWrapper>
          </ReportExportProvider>
        </Styled.StyledModal>
      </Styled.Container>
    </ReportingLayout>
  );
};

export default Reporting;
